import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  buildGuideReferenceRows,
  docTypeLabel,
  fmtMoney,
  fmtUnitPrice,
  fmtQty,
  formatGuideDate,
  formatWarehouseRef,
  getGuideCurrencySymbol,
  getGuideStatusInfo,
  getGuideTableColumns,
  getGuideTitle,
  getItemDescription,
  getItemProject,
  getGuideItemQuantityDisplay,
  getGuideItemDocumentUnitPrice,
  getGuideItemSubtotalDisplay,
  getGuideTotalDisplay,
  getSortedGuideItems,
  shouldHideProjectColumn,
  shouldShowGuideTotal,
} from './entryGuideDocumentHelpers';
import { formatGuideEntryDateTimeLima } from './dateUtils';
import {
  ITEMS_TABLE,
  PDF_COL_WIDTHS_5,
  PDF_COL_WIDTHS_6,
  PRINT_MARGIN_MM,
} from './entryGuidePrintLayout';

const TEAL = [13, 148, 136];
const GRAY = [55, 65, 81];
const GRAY_LIGHT = [156, 163, 175];
const BG_TABLE_ALT = [249, 250, 251];
const MARGIN = PRINT_MARGIN_MM;
const HEADER_FONT = 7.5;

function measureSegments(doc, segments) {
  let width = 0;
  segments.forEach((seg) => {
    doc.setFont('helvetica', seg.style === 'label' ? 'bold' : 'normal');
    doc.setFontSize(HEADER_FONT);
    width += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return width;
}

function drawSegments(doc, startX, y, segments) {
  let x = startX;
  segments.forEach((seg) => {
    if (seg.style === 'label') {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...TEAL);
    } else if (seg.style === 'sep') {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_LIGHT);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY);
    }
    doc.setFontSize(HEADER_FONT);
    doc.text(seg.text, x, y);
    x += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return x;
}

function drawStatusBadge(doc, x, y, statusText, tone) {
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  const padX = 2;
  const textW = doc.getTextWidth(statusText);
  const badgeW = textW + padX * 2;
  const badgeH = 3.8;

  const tones = {
    issued: { fill: [209, 250, 229], text: [6, 95, 70] },
    open: { fill: [254, 243, 199], text: [146, 64, 14] },
    completed: { fill: [219, 234, 254], text: [30, 64, 175] },
    cancelled: { fill: [254, 226, 226], text: [153, 27, 27] },
  };
  const colors = tones[tone] || tones.issued;

  doc.setFillColor(...colors.fill);
  doc.setTextColor(...colors.text);
  doc.roundedRect(x, y - badgeH + 1.2, badgeW, badgeH, 1.2, 1.2, 'F');
  doc.text(statusText, x + padX, y);
  return x + badgeW + 2;
}

function drawSectionTitle(doc, margin, y, title) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...TEAL);
  doc.text(title.toUpperCase(), margin, y);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.3);
  doc.line(margin, y + 1.2, margin + doc.getTextWidth(title.toUpperCase()), y + 1.2);
}

function buildReferenceLineSegments(guide) {
  return buildGuideReferenceRows(guide).map((ref) => {
    const segments = [
      { text: `${ref.type}:`, style: 'label' },
      { text: ref.code, style: 'value' },
    ];
    if (ref.detail && ref.detail !== '—') {
      segments.push({ text: '·', style: 'sep' }, { text: ref.detail, style: 'value' });
    }
    return segments;
  });
}

function drawReferencesSection(doc, guide, startY, pageWidth, pageHeight) {
  const lineSegments = buildReferenceLineSegments(guide);
  if (!lineSegments.length) return startY;

  let y = startY;
  const contentWidth = pageWidth - MARGIN * 2;
  const lineHeight = 3.5;

  if (y + 8 > pageHeight - MARGIN) {
    doc.addPage();
    y = MARGIN + 4;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...TEAL);
  doc.text('Referencias', MARGIN, y);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y + 1.2, MARGIN + doc.getTextWidth('Referencias'), y + 1.2);
  y += 4;

  lineSegments.forEach((segments) => {
    if (y + lineHeight > pageHeight - MARGIN) {
      doc.addPage();
      y = MARGIN + 4;
    }

    if (measureSegments(doc, segments) > contentWidth) {
      const fullLine = segments
        .map((seg) => (seg.style === 'sep' ? ` ${seg.text} ` : seg.text))
        .join('')
        .replace(/:  /, ': ');
      const wrapped = doc.splitTextToSize(fullLine, contentWidth);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(HEADER_FONT);
      doc.setTextColor(...GRAY);
      doc.text(wrapped.slice(0, 3), MARGIN, y);
      y += wrapped.length * lineHeight;
      return;
    }

    drawSegments(doc, MARGIN, y, segments);
    y += lineHeight;
  });

  return y + 2;
}

function buildPdfItemRows(guide, currencySymbol) {
  const hideProject = shouldHideProjectColumn(guide);

  return getSortedGuideItems(guide).map((item) => {
    const row = [
      getItemDescription(item),
      item.unit_of_measure || item.product?.unit_of_measure || 'UN',
    ];

    if (!hideProject) {
      row.push(getItemProject(item));
    }

    row.push(
      getGuideItemQuantityDisplay(guide, item),
      `${currencySymbol} ${fmtUnitPrice(getGuideItemDocumentUnitPrice(item))}`,
      getGuideItemSubtotalDisplay(guide, item),
    );

    return row;
  });
}

function getTableColumnStyles(guide) {
  if (shouldHideProjectColumn(guide)) {
    const w = PDF_COL_WIDTHS_5;
    return {
      0: { cellWidth: w.description },
      1: { cellWidth: w.unit, halign: 'center' },
      2: { cellWidth: w.quantity, halign: 'right' },
      3: { cellWidth: w.unitPrice, halign: 'right' },
      4: { cellWidth: w.subtotal, halign: 'right', fontStyle: 'bold' },
    };
  }

  const w = PDF_COL_WIDTHS_6;
  return {
    0: { cellWidth: w.description },
    1: { cellWidth: w.unit, halign: 'center' },
    2: { cellWidth: w.project },
    3: { cellWidth: w.quantity, halign: 'right' },
    4: { cellWidth: w.unitPrice, halign: 'right' },
    5: { cellWidth: w.subtotal, halign: 'right', fontStyle: 'bold' },
  };
}

function getAmountsRightX(doc, guide) {
  const subtotalColIndex = shouldHideProjectColumn(guide) ? 4 : 5;
  const subtotalCol = doc.lastAutoTable?.table?.columns?.[subtotalColIndex];
  if (subtotalCol) return subtotalCol.x + subtotalCol.width - 2;

  if (shouldHideProjectColumn(guide)) {
    const w = PDF_COL_WIDTHS_5;
    return MARGIN + w.description + w.unit + w.quantity + w.unitPrice + w.subtotal - 2;
  }

  const w = PDF_COL_WIDTHS_6;
  return MARGIN + w.description + w.unit + w.project + w.quantity + w.unitPrice + w.subtotal - 2;
}

function drawFooter(doc, guide, startY, pageWidth, pageHeight, amountsRightX, currencySymbol) {
  const contentWidth = pageWidth - MARGIN * 2;
  const obsWidth = contentWidth * 0.52;
  const totalsWidth = contentWidth * 0.44;
  const gap = contentWidth - obsWidth - totalsWidth;
  const totalsX = MARGIN + obsWidth + gap;
  const footerHeight = 14;

  let y = startY;
  if (y + footerHeight > pageHeight - MARGIN) {
    doc.addPage();
    y = MARGIN + 4;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...TEAL);
  doc.text('Observaciones:', MARGIN, y + 4);

  if (guide.notes) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    const noteLines = doc.splitTextToSize(String(guide.notes), obsWidth);
    doc.text(noteLines.slice(0, 4), MARGIN, y + 8);
  }

  if (shouldShowGuideTotal(guide)) {
    const ty = y + 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(HEADER_FONT);
    doc.setTextColor(...TEAL);
    doc.text('Total:', totalsX, ty);
    doc.setTextColor(...GRAY);
    doc.text(getGuideTotalDisplay(guide), amountsRightX, ty, { align: 'right' });
  }
}

export async function downloadEntryGuidePdf(guide) {
  if (!guide) {
    throw new Error('No hay datos de la guía para generar el PDF');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const currencySymbol = getGuideCurrencySymbol(guide);
  const status = getGuideStatusInfo(guide);
  const isAjuste = guide.guide_type === 'ajuste';
  const isTraspaso = guide.guide_type === 'traspaso';

  let y = MARGIN + 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...TEAL);
  doc.text(getGuideTitle(guide), pageWidth / 2, y, { align: 'center' });

  y += 3;
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 4;

  const line1 = [
    { text: 'Guía:', style: 'label' },
    { text: guide.code || '-', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Fecha:', style: 'label' },
    {
      text:
        formatGuideEntryDateTimeLima(guide.entry_date || guide.issue_date, guide.entry_time)
        || formatGuideDate(guide.issue_date)
        || '—',
      style: 'value',
    },
    { text: '·', style: 'sep' },
    { text: 'Estado:', style: 'label' },
  ];
  const estadoX = MARGIN + measureSegments(doc, line1);
  drawSegments(doc, MARGIN, y, line1);
  drawStatusBadge(doc, estadoX, y, status.label, status.tone);

  y += 4;
  const warehouseLine = [
    { text: isTraspaso ? 'Almacén origen:' : 'Almacén:', style: 'label' },
    { text: formatWarehouseRef(guide.warehouse) || '—', style: 'value' },
  ];
  if (isTraspaso && guide.destination_warehouse) {
    warehouseLine.push(
      { text: '·', style: 'sep' },
      { text: 'Destino:', style: 'label' },
      { text: formatWarehouseRef(guide.destination_warehouse), style: 'value' },
    );
  }
  drawSegments(doc, MARGIN, y, warehouseLine);

  y += 4;
  const entityLine = [
    { text: 'Entidad:', style: 'label' },
    { text: guide.supplier?.name || (isAjuste ? '—' : 'N/A'), style: 'value' },
  ];
  if (guide.supplier?.document_number) {
    entityLine.push(
      { text: '·', style: 'sep' },
      { text: `${docTypeLabel(guide.supplier)}:`, style: 'label' },
      { text: guide.supplier.document_number, style: 'value' },
    );
  }
  if (!isAjuste && guide.currency) {
    entityLine.push(
      { text: '·', style: 'sep' },
      { text: 'Moneda:', style: 'label' },
      { text: guide.currency === 'USD' ? 'USD' : 'PEN', style: 'value' },
    );
  }
  drawSegments(doc, MARGIN, y, entityLine);

  if (
    guide.entry_date
    && guide.issue_date
    && formatGuideDate(guide.entry_date) !== formatGuideDate(guide.issue_date)
  ) {
    y += 4;
    drawSegments(doc, MARGIN, y, [
      { text: 'Fecha de emisión:', style: 'label' },
      { text: formatGuideDate(guide.issue_date), style: 'value' },
    ]);
  }

  y += 4;
  y = drawReferencesSection(doc, guide, y, pageWidth, pageHeight);

  y += 3;
  drawSectionTitle(doc, MARGIN, y, 'Items de la Guía');
  y += 4;

  const tableBody = buildPdfItemRows(guide, currencySymbol);
  const tableHead = [getGuideTableColumns(guide)];

  if (tableBody.length === 0) {
    const emptyRow = shouldHideProjectColumn(guide)
      ? ['Sin ítems registrados', '—', '—', '—', '—']
      : ['Sin ítems registrados', '—', '—', '—', '—', '—'];
    tableBody.push(emptyRow);
  }

  autoTable(doc, {
    startY: y,
    head: tableHead,
    body: tableBody,
    margin: { left: MARGIN, right: MARGIN, top: MARGIN, bottom: 10 },
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: ITEMS_TABLE.bodyFontSize,
      cellPadding: ITEMS_TABLE.cellPadding,
      minCellHeight: ITEMS_TABLE.minCellHeight,
      textColor: GRAY,
      lineColor: [229, 231, 235],
      lineWidth: 0.15,
      valign: 'middle',
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: TEAL,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: ITEMS_TABLE.headFontSize,
      halign: 'left',
      cellPadding: { top: 1.2, right: 2, bottom: 1.2, left: 2 },
    },
    columnStyles: getTableColumnStyles(guide),
    alternateRowStyles: { fillColor: BG_TABLE_ALT },
    showHead: 'everyPage',
    didDrawPage: (data) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...GRAY_LIGHT);
      doc.text(
        `Guía ${guide.code || ''} — Pág. ${data.pageNumber}`,
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' },
      );
    },
  });

  const tableEndY = doc.lastAutoTable.finalY + 8;
  const amountsRightX = getAmountsRightX(doc, guide);
  drawFooter(doc, guide, tableEndY, pageWidth, pageHeight, amountsRightX, currencySymbol);

  const safeCode = String(guide.code || guide.id || 'guia').replace(/[^\w.-]+/g, '_');
  doc.save(`Guia_${safeCode}.pdf`);
}
